import React, { useEffect, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import clsx from 'clsx';
import { connect, useSelector } from 'react-redux';
import { IState } from 'services/root-reducer';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AutoScrollbars, Panel, PanelBody, PanelHeader } from 'components';
import { getOperation } from 'selectors';
import { routes } from 'routes';
import { actions as searchActions } from 'services/search/search-service';
import { actions as mapActions } from 'services/map/map-service';
import { IOperation } from 'types';
import { SearchType } from 'services/search/search-reducer';
import { Logo } from './logo';

import { CategorySelector } from './category-selector';
import { CategorySelectorLite } from './category-selector-lite';
import { CategoryPanel } from './category-panel';
import { categoriesConfig, isCategoryAvailable } from './categories-config';
import { SearchResults } from './search-results';
import './style.scss';

interface ISideBarProps {
    initialize: IOperation;
    searchState: IState['search'];
    searchOperation: IOperation;
}

const mapStateToProps = (state: IState) => ({
    initialize: getOperation(state, 'APP_INITIALIZE'),
    searchOperation: getOperation(state, 'SEARCH_OBJECTS_OMS'),
    searchState: state.search
});

const SideBarComponent: React.FC<ISideBarProps> = ({
    searchState,
    searchOperation
}): JSX.Element => {
    const { container } = useParams();
    let headerRef = React.createRef<HTMLDivElement>();
    const [ searchEnabled, setSearchEnabled ] = useState<boolean>(false);
    const { t } = useTranslation();

    useEffect(() => {
        document.title = t(container);
    }, [ container ]);

    const filtersWindowVisible = useSelector((state: IState) => state.app.filtersWindowVisible);

    const onBackButtonClick = () => {
        searchActions.clearState();
        mapActions.highlightCadastreGeometry(null);
        routes.redirect(routes.map, { container: 'root' });
    };

    const renderSearch = () => <SearchResults />;

    const performSearch = (search_type = searchState.search_type) => {
        if (search_type === SearchType.Objects) {
            if (/^\d*:/.test(searchState.search_input_value)) {
                searchActions.searchPkkObjects(searchState.search_input_value);
            } else {
                searchActions.searchObjects(searchState.search_input_value);
            }
        } else if (search_type === SearchType.Address) {
            searchActions.searchAddress(searchState.search_input_value);
        } else if (search_type === SearchType.Layers) {
            searchActions.searchLayer(searchState.search_input_value);
        } else if (search_type === SearchType.PKK) {
            searchActions.searchPkk(searchState.search_input_value);
        }
    };

    const renderCategories = () => (
        <React.Fragment>
            {isCategoryAvailable(container) ? (
                <CategorySelectorLite
                    currentCategory={container}
                    onCategoryClick={(category) => {
                        routes.redirect(routes.map, {
                            container: category
                        });
                    }}
                    disableHints={isMobile}
                />
            ) : (
                <CategorySelector
                    onCategoryClick={(category) => {
                        routes.redirect(routes.map, {
                            container: category
                        });
                    }}
                />
            )}
            <PanelBody className="oms-sidebar-body">
                {(container !== 'root') && (
                    <AutoScrollbars>
                        <CategoryPanel category={container} />
                    </AutoScrollbars>
                )}
            </PanelBody>
        </React.Fragment>
    );

    return (
        <Panel
            className={clsx('oms-sidebar-panel', {
                'hidden': isTablet && filtersWindowVisible
            })}
        >
            <div className="oms-sidebar-panel__wrapper">
                <PanelHeader
                    className={clsx('oms-sidebar-header')}
                    ref={headerRef}
                >
                    <Logo
                        showBackButton={container !== 'root'}
                        logoText={t('Ленинградская область')}
                        inputValue={searchState.search_input_value}
                        activeTab={searchState.search_type}
                        onTabChange={(value) => {
                            searchActions.setSearchType(value);
                            mapActions.placeYaAddressMarker(null);
                            if (searchState.search_input_value.length) {
                                performSearch(value);
                            }
                        }}
                        searchOperation={searchOperation}
                        onInputValueChange={(value) => {
                            searchActions.setSearchInputValue(value);
                        }}
                        onPerformSearch={performSearch}
                        onBackButtonClick={onBackButtonClick}
                        onSearchEnabled={() => {
                            setSearchEnabled(true);
                        }}
                        onSearchDisabled={() => {
                            searchActions.clearState();
                            mapActions.highlightCadastreGeometry(null);
                            mapActions.placeYaAddressMarker(null);
                            setSearchEnabled(false);
                        }}
                    />
                </PanelHeader>
                {searchEnabled ? renderSearch() : renderCategories() }
            </div>
        </Panel>
    );
};

export const SideBar = connect(mapStateToProps)(SideBarComponent);
